import React, { useState, useEffect, useMemo } from 'react';
import {
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Stack,
    Chip,
    Grid,
    CircularProgress, FormHelperText,
} from '@mui/material';
import { AnalogTimePicker } from '../../../../../Components/Elements/AnalogTimePicker';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSelector, useDispatch } from 'react-redux';
import { CardActions, CardContent, CardHeader } from '@mui/material';
import { DialogCard } from '../../../../../Components';
import { addRosterEntry, updateRosterEntry, deleteRosterEntry } from '../../Redux/Action';
import dayjs from 'dayjs';
import {cleanName, getRosterEditBlockReason} from "../../../../../Setup/Config/formats";
import {setModal} from "../../../../Common/App";
import {Loader, Alert} from "../../../../../Components";
import { usePermissions } from '../../../../../Hooks/usePermissions';

export const RosterModal = ({item, date}) => {

    const dispatch = useDispatch();
    const {hasPermission} = usePermissions();
    const statuses = useSelector(state => state.app.config.statuses || []);
    const events = useSelector(state => state.app.config.events || []);
    const venues = useSelector(state => state.app.config.venues || []);
    const locations = useSelector(state => state.app.config.locations || []);
    const rosters = useSelector(state => state.rosters?.list || []);
    const rosterSettings = useSelector(state => state.app.settings || {});
    const canEditLocked = hasPermission('roster.roster.edit-locked-entries');
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    // Check if roster exists for this date - rosters is an object, not array
    const existingRoster = item.rosters && typeof item.rosters === 'object' && 
    item.rosters[date] ? item.rosters[date] : null;

    const [data, setData] = useState({
        user_id: '',
        roster_date: dayjs(),
        start_time: null,
        end_time: null,
        status_id: '',
        event_id: '',
        venue_id: '',
        location_id: '',
    });

    const isLocked = () => Boolean(getRosterEditBlockReason({
        clickedDate: date,
        settings: rosterSettings,
        rosters,
        rosterEntry: existingRoster,
        canEditLocked,
    }));

    useEffect(() => {

        if (item && date) {

            const rosterEntryForDate = item.rosters && typeof item.rosters === 'object' &&
                item.rosters[date] ? item.rosters[date] : null;

            const blockReason = getRosterEditBlockReason({
                clickedDate: date,
                settings: rosterSettings,
                rosters,
                rosterEntry: rosterEntryForDate,
                canEditLocked,
            });

            if (blockReason) {
                setErrors({ roster_date: blockReason });
                setMessage(blockReason);
                setStatus('error');
                return;
            }

            if (rosterEntryForDate) {
                
                let statusId = '';
                if (typeof rosterEntryForDate.status === 'string') {
                    // Old format: status is string
                    const statusObj = statuses.find(s => s.name === rosterEntryForDate.status);
                    statusId = statusObj ? statusObj.id : '';
                } else if (rosterEntryForDate.status?.id) {
                    // New format: status is object with id
                    statusId = rosterEntryForDate.status.id;
                }
                
                setData({
                    user_id: rosterEntryForDate.user_id || item.id,
                    roster_date: dayjs(date),
                    start_time: rosterEntryForDate.start_time ? dayjs(rosterEntryForDate.start_time, 'HH:mm:ss') : null,
                    end_time: rosterEntryForDate.end_time ? dayjs(rosterEntryForDate.end_time, 'HH:mm:ss') : null,
                    status_id: statusId,
                    event_id: rosterEntryForDate.event_id || '',
                    venue_id: rosterEntryForDate.venue_id || '',
                    location_id: rosterEntryForDate.location_id || '',
                });
            } else {
                // Add mode - populate with selected user and date
                setData({
                    user_id: item.id,
                    roster_date: dayjs(date),
                    start_time: null,
                    end_time: null,
                    status_id: '',
                    event_id: '',
                    venue_id: '',
                    location_id: '',
                });
            }
            setErrors({});
        }
    }, [item, date, canEditLocked, rosterSettings, rosters, statuses]);

    const rosterStatuses = () => {
        return (statuses.filter((status) => status.status_type?.name === 'ROSTER_ENTRY') || [])
            .sort((a, b) => a.name.localeCompare(b.name));
    };

    const filteredLocations = useMemo(() => {
        if (!data.venue_id) {
            return [];
        }
        return locations.filter((loc) => loc.venue_id === data.venue_id);
    }, [locations, data.venue_id]);

    // Handle form field changes
    const handleChange = (e, field = null, value = null) => {
        // If e is an event object (regular form field)
        if (e && e.target) {
            const { name, value } = e.target;
            setData((prev) => {
                const next = { ...prev, [name]: value };
                if (name === 'venue_id') {
                    next.location_id = '';
                }
                return next;
            });
            
            // Clear error when field is updated
            if (errors[e.target.name]) {
                setErrors(prev => ({
                    ...prev,
                    [e.target.name]: ''
                }));
            }
        } 
        // If e is a field name (date/time field)
        else if (field && value !== null) {
            setData(data => ({
                ...data,
                [field]: value
            }));
            
            // Clear error when field is updated
            if (errors[field]) {
                setErrors(prev => ({
                    ...prev,
                    [field]: ''
                }));
            }
        }
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!data.user_id) {
            newErrors.user_id = 'User is required';
        }

        if (!data.roster_date) {
            newErrors.roster_date = 'Date is required';
        }

        if (!data.status_id) {
            newErrors.status_id = 'Status is required';
        }

        // Validate time range for specific statuses
        if (data.status_id) {
            const selectedStatus = statuses.find(s => s.id === data.status_id);
            
            if (selectedStatus?.name === 'ON_DUTY') {
                if (!data.start_time) {
                    newErrors.start_time = 'Start time is required for time range';
                }
                if (!data.end_time) {
                    newErrors.end_time = 'End time is required for time range';
                }
                if (data.start_time && data.end_time && 
                    data.start_time.isAfter(data.end_time)) {
                    newErrors.end_time = 'End time must be after start time';
                }
            }
            
            if (selectedStatus?.name === '24_HOURS') {
                // For 24 hours, set time range automatically
                setData(data => ({
                    ...data,
                    start_time: dayjs().startOf('day'),
                    end_time: dayjs().endOf('day')
                }));
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setIsLoading(true);

        const payload = {
            ...data,
            roster_date: data.roster_date.format('YYYY-MM-DD'),
            start_time: data.start_time ? data.start_time.format('HH:mm:ss') : null,
            end_time: data.end_time ? data.end_time.format('HH:mm:ss') : null,
        };

        if (existingRoster) {
            // Update existing roster with ID
            await updateRosterEntry({dispatch, data: { ...payload, id: existingRoster.id }}).then((res) => {
                if(res.success) {
                    dispatch(setModal({component:null, open:false}));
                }
                setIsLoading(false);
                setErrors(res.errors);
                setMessage(res.message);
                setStatus(res.status);
            });
        } else {
            // Create new roster
            await addRosterEntry({dispatch, data: payload}).then((res) => {
                if(res.success) {
                    dispatch(setModal({component:null, open:false}));
                }
                setIsLoading(false);
                setErrors(res.errors);
                setMessage(res.message);
                setStatus(res.status);
            });
        }
    };

    // Check if this is edit mode - rosters is an object, not array
    const isEditMode = item.rosters && typeof item.rosters === 'object' && 
        Object.keys(item.rosters).some(rosterDate => rosterDate === date);

    // Handle delete roster
    const handleDeleteRoster = async () => {
        const existingRoster = item.rosters && typeof item.rosters === 'object' && item.rosters[date] ? item.rosters[date] : null;
        if (existingRoster) {
            setIsLoading(true);
            await deleteRosterEntry({dispatch, data: {id: existingRoster.id}}).then((res) => {
                if(res.success) {
                    dispatch(setModal({component:null, open:false}));
                }
                setIsLoading(false);
                setErrors(res.errors);
                setMessage(res.message);
                setStatus(res.status);
            });
        }
    };
    
    return (
        <DialogCard>
            <CardHeader
                subheader={`${data.roster_date.format('MMM D, YYYY')}`}
                title={"Roster Entry"} 
                avatar={<Loader isLoading={isLoading} type={"linear"}/>}
                action={
                    isLocked() && (
                        <Chip color={"warning"} label={"Locked"} />
                    )
                }
            />
            <CardContent>
                <Grid container spacing={2}>
                    {message && (
                        <Grid item xs={12}>
                            <Alert severity={status} duration={-1} showCloseButton={false}>
                                {message}
                            </Alert>
                        </Grid>
                    )}
                    {/* Status Selection */}
                    <Grid item xs={12}>
                        <FormControl fullWidth error={!!errors.status_id}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={data.status_id}
                                onChange={(e) => handleChange(e, 'status_id')}
                                name="status_id"
                                label="Status"
                                variant={"outlined"}
                            >
                                {rosterStatuses().map((status) => (
                                    <MenuItem key={status.id} value={status.id}>
                                        {cleanName(status.name)}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>{errors.status_id}</FormHelperText>
                        </FormControl>
                    </Grid>
                    
                    {/* Event Selection */}
                    <Grid item xs={12}>
                        <FormControl fullWidth error={!!errors.event_id}>
                            <InputLabel>Event</InputLabel>
                            <Select
                                value={data.event_id}
                                onChange={(e) => handleChange(e, 'event_id')}
                                name="event_id"
                                label="Event"
                                variant="outlined"
                            >
                                <MenuItem key={""} value={""}>Select Event</MenuItem>
                                {events.map((event) => (
                                    <MenuItem key={event.id} value={event.id}>
                                        {cleanName(event.event_name)}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>{errors.event_id}</FormHelperText>
                        </FormControl>
                    </Grid>

                    {/* Venue Selection */}
                    <Grid item xs={12}>
                        <FormControl fullWidth error={!!errors.venue_id}>
                            <InputLabel>Venue</InputLabel>
                            <Select
                                value={data.venue_id}
                                onChange={(e) => handleChange(e, 'venue_id')}
                                name="venue_id"
                                label="Venue"
                                variant="outlined"
                            >
                                <MenuItem key={""} value={""}>Select Venue</MenuItem>
                                {venues.map((venue) => (
                                    <MenuItem key={venue.id} value={venue.id}>
                                        {cleanName(venue.venue_name)}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>{errors.venue_id}</FormHelperText>
                        </FormControl>
                    </Grid>

                    {/* Location Selection — filtered by selected venue */}
                    <Grid item xs={12}>
                        <FormControl fullWidth error={!!errors.location_id} disabled={!data.venue_id}>
                            <InputLabel>Location</InputLabel>
                            <Select
                                value={data.venue_id ? data.location_id : ''}
                                onChange={(e) => handleChange(e, 'location_id')}
                                name="location_id"
                                label="Location"
                                variant="outlined"
                            >
                                <MenuItem value="">Select Location</MenuItem>
                                {filteredLocations.map((location) => (
                                    <MenuItem key={location.id} value={location.id}>
                                        {cleanName(location.location_name)}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>
                                {errors.location_id
                                    || (!data.venue_id ? 'Select a venue first' : '')}
                            </FormHelperText>
                        </FormControl>
                    </Grid>

                    {/* Time Range - Only show for ON_DUTY status */}
                    {data.status_id && rosterStatuses().find(s => s.id === data.status_id)?.name === 'ON_DUTY' && (
                        <Grid item xs={12}>
                            <Box display="flex" gap={2} alignItems="center">
                                <AnalogTimePicker
                                    label="Start Time"
                                    value={data.start_time}
                                    onChange={(value) => handleChange(null, 'start_time', value)}
                                    fullWidth
                                    slotProps={{
                                        textField: {
                                            error: !!errors.start_time,
                                            helperText: errors.start_time || '',
                                        },
                                    }}
                                />
                                <Typography variant="body2" color="text.secondary">
                                    to
                                </Typography>
                                <AnalogTimePicker
                                    label="End Time"
                                    value={data.end_time}
                                    onChange={(value) => handleChange(null, 'end_time', value)}
                                    fullWidth
                                    slotProps={{
                                        textField: {
                                            error: !!errors.end_time,
                                            helperText: errors.end_time || '',
                                        },
                                    }}
                                />
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </CardContent>
            <CardActions>
                <Stack direction="row" spacing={2}> 
                    <Button onClick={() => dispatch(setModal({component:null, open:false}))}>Cancel</Button>
                    
                    {/* Delete Button - Only show in edit mode */}
                    {isEditMode && (
                        <Button 
                            variant="outlined" 
                            color="error"
                            disabled={isLoading || isLocked()}
                            onClick={handleDeleteRoster} // pass id
                            startIcon={<DeleteIcon />}
                        >
                            Delete
                        </Button>
                    )}
                </Stack>

                <Button 
                    disabled={isLoading || isLocked()}
                    variant="contained" 
                    color={'primary'} 
                    onClick={handleSubmit}
                    startIcon={isLoading ? <CircularProgress size={16} /> : null}
                >
                    {isLoading ? 'Saving...' : (isEditMode ? 'Update' : 'Create')}
                </Button>
            </CardActions>
        </DialogCard>
    );
};